import React, { useState, useEffect } from 'react';
import { Menu, X, LogOut } from 'lucide-react';
import Logo from '../ui/Logo';

// Shared collapsible-sidebar shell for the therapist and client dashboards —
// same layout/theme as the admin dashboard, so all three feel like one product.
const DashboardShell = ({ title, navItems, activeTab, onTabChange, onLogout, children }) => {
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const NavButton = ({ item }) => {
    const active = activeTab === item.id;
    return (
      <button
        onClick={() => {
          onTabChange(item.id);
          if (isMobile) setIsOpen(false);
        }}
        className={`flex items-center w-full py-2 px-3 rounded-lg text-sm transition-colors ${
          active ? 'bg-accent-hard text-primary-dark font-semibold' : 'text-secondary/80 hover:bg-primary-light/40 hover:text-secondary'
        } ${!isOpen ? 'justify-center' : ''}`}
        title={!isOpen ? item.label : ''}
      >
        <item.icon className={isOpen ? 'mr-3 shrink-0' : 'shrink-0'} size={20} />
        {isOpen && <span className="truncate">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-primary-lighter flex">
      <div className={`bg-primary-dark h-screen fixed left-0 top-0 z-50 transition-all duration-300 flex flex-col ${
        isOpen ? 'w-64' : 'w-16'
      } ${isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}`}>
        {/* Desktop-only collapse toggle — mobile has its own hamburger in the top bar below,
            so this would otherwise show as a confusing second icon on small screens. */}
        {!isMobile && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-0 top-6 translate-x-1/2 bg-primary-dark border border-primary-light/30 rounded-full p-1.5 hover:bg-primary-light z-50"
          >
            {isOpen ? <X className="text-secondary" size={18} /> : <Menu className="text-secondary" size={18} />}
          </button>
        )}

        <div className={`px-4 pt-6 pb-4 border-b border-primary-light/20 ${!isOpen ? 'flex justify-center' : ''}`}>
          {isOpen ? <Logo variant="light" /> : <span className="text-accent-light font-heading text-xl font-bold">S</span>}
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navItems.map((item) => <NavButton key={item.id} item={item} />)}
        </nav>

        <div className="p-3 border-t border-primary-light/20">
          <button
            onClick={onLogout}
            className={`flex items-center w-full py-2 px-3 rounded-lg text-sm text-secondary/80 hover:bg-red-900/40 hover:text-red-200 ${
              !isOpen ? 'justify-center' : ''
            }`}
            title={!isOpen ? 'Logout' : ''}
          >
            <LogOut className={isOpen ? 'mr-3 shrink-0' : 'shrink-0'} size={20} />
            {isOpen && 'Logout'}
          </button>
        </div>
      </div>

      {/* Mobile overlay while the sidebar is open */}
      {isMobile && isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsOpen(false)} />
      )}

      <div className={`flex-1 min-w-0 transition-all duration-300 ${isMobile ? 'ml-0' : isOpen ? 'ml-64' : 'ml-16'}`}>
        {isMobile && (
          <div className="sticky top-0 z-30 bg-white border-b px-4 py-3 flex items-center gap-3 shadow-sm">
            <button onClick={() => setIsOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-100">
              <Menu size={22} />
            </button>
            <h1 className="font-semibold text-dim">{title}</h1>
          </div>
        )}
        <main className="p-4 sm:p-6 max-w-5xl mx-auto">
          {!isMobile && <h1 className="text-2xl font-bold text-dim mb-6">{title}</h1>}
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardShell;
